import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/site/Reveal";
import { PROJECT_MEDIA } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const ProjectCard = ({ project, index, viewLabel }) => (
    <Reveal
        delay={index * 0.12}
        className={project.featured ? "md:col-span-2" : ""}
    >
        <article
            className="group glass-card glass-card-hover rounded-2xl overflow-hidden h-full flex flex-col transition-transform duration-500 hover:-translate-y-2 hover:shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_24px_rgba(0,210,255,0.12)]"
            data-testid={`project-card-${index}`}
        >
            <div className={`relative overflow-hidden ${project.featured ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
                <img
                    src={project.image}
                    alt={`${project.title} project preview`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030305]/90 via-[#030305]/20 to-transparent" />
                <div className="absolute inset-0 bg-cyan-400/0 transition-colors duration-500 group-hover:bg-cyan-400/5" />
            </div>
            <div className="flex flex-1 flex-col p-6 md:p-8">
                <h3 className="font-display text-2xl font-bold text-white mb-2">
                    {project.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed font-light mb-5 flex-1">
                    {project.description}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                        {project.tech.map((tag) => (
                            <span
                                key={tag}
                                className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-zinc-400"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                    <button
                        className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-cyan-400 transition-colors duration-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                        data-testid={`project-view-button-${index}`}
                        aria-label={`${viewLabel}: ${project.title}`}
                    >
                        {viewLabel}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </div>
            </div>
        </article>
    </Reveal>
);

export const Portfolio = () => {
    const { t } = useLanguage();

    return (
        <section id="portfolio" className="relative py-24 md:py-32 bg-surface/40" data-testid="portfolio-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <SectionHeader
                    number={t.portfolio.number}
                    label={t.portfolio.label}
                    title={t.portfolio.title}
                    description={t.portfolio.description}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {PROJECT_MEDIA.map((media, i) => (
                        <ProjectCard
                            key={t.portfolio.projects[i].title}
                            project={{ ...media, ...t.portfolio.projects[i] }}
                            index={i}
                            viewLabel={t.portfolio.viewProject}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
